import { useEffect, useState } from 'react';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	FREE_EDITION_KEY,
	STANDARD_EDITION_KEY,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { MULTI_PRODUCT_UPGRADE_MSG_ID, MULTI_PRODUCT_UPGRADE_MSG_ID_STG } from './constants.tsx';
import type { EligibilityCheckObject, ProductEntitlements } from './types.tsx';
import { fetchEntitlements } from './utils.tsx';
import { useCoordinationClient } from './utils/coordination-client.tsx';

const getEligibilityData = async (cloudId: string): Promise<EligibilityCheckObject | null> => {
	const eligibility: EligibilityCheckObject = {};
	const entitlements: ProductEntitlements = await fetchEntitlements(cloudId);

	const { serviceDesk, confluence, jiraSoftware } = entitlements;

	const hasFreeEdition =
		serviceDesk?.edition === FREE_EDITION_KEY || confluence?.edition === FREE_EDITION_KEY;

	if (!jiraSoftware || jiraSoftware.edition !== STANDARD_EDITION_KEY || !hasFreeEdition) {
		return null;
	}

	// identify which product we are on
	if (serviceDesk?.edition === FREE_EDITION_KEY) {
		eligibility.productKey = serviceDesk.productKey;
		eligibility.entitlementDetails = {
			billingAccountId: serviceDesk.billingAccountId,
			billingSourceSystem: serviceDesk.billingSystem,
			entitlementId: serviceDesk.ccpEntitlementId,
		};
	} else if (confluence?.edition === FREE_EDITION_KEY) {
		eligibility.productKey = confluence.productKey;
		eligibility.entitlementDetails = {
			billingAccountId: confluence.billingAccountId,
			billingSourceSystem: confluence.billingSystem,
			entitlementId: confluence.ccpEntitlementId,
		};
	}

	return eligibility;
};

export const useMultiProductUpgrade = () => {
	const environment = useEnvironment();
	const isAdmin = useIsAdmin();
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isLoading, setIsLoading] = useState(true);
	const [isEnrolled, setIsEnrolled] = useState(false);
	const [eligibilityData, setEligibilityData] = useState<EligibilityCheckObject | null>(null);
	const { productKey, entitlementDetails } = eligibilityData || {};
	const { hasMessageStarted, setEpMessageStarted, stopMessage } = useCoordinationClient(
		environment !== 'prod' ? MULTI_PRODUCT_UPGRADE_MSG_ID_STG : MULTI_PRODUCT_UPGRADE_MSG_ID,
	);

	useEffect(() => {
		if (hasMessageStarted === null) {
			setEpMessageStarted();
		}
	}, [hasMessageStarted, setEpMessageStarted]);

	useEffect(() => {
		if (!isAdmin) {
			setIsLoading(false);
			return;
		}

		if (hasMessageStarted === false) {
			setIsLoading(false);
			return;
		}

		if (hasMessageStarted) {
			getEligibilityData(cloudId)
				.then((eligibilityResult) => {
					setEligibilityData(eligibilityResult);
					if (eligibilityResult) {
						setIsEnrolled(true);
					} else {
						setIsEnrolled(false);
					}
				})
				.catch(() => {
					fireOperationalAnalytics(createAnalyticsEvent({}), 'useMultiProductUpgrade failed', {
						errorMessage: 'fetchFailed',
					});
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [cloudId, isLoading, isAdmin, createAnalyticsEvent, hasMessageStarted]);

	return {
		billingAccountId: entitlementDetails?.billingAccountId,
		billingSystem: entitlementDetails?.billingSourceSystem,
		cloudId,
		entitlementId: entitlementDetails?.entitlementId,
		isEnrolled,
		isLoading,
		productKey,
		onStopMessage: stopMessage,
	};
};

export default useMultiProductUpgrade;
